// /walkin/:slug — 치과 데스크 QR 착지 「접수」 (2026-10-03 대표 지시로 개편 — 정본: 승인된 시뮬레이터 qr_waiting_sim.html 환자 휴대폰 화면)
// 같은 QR 주소 유지. 흐름: [처음 왔어요]/[다녀간 적 있어요] → 입력 → 접수 → "지금 대기 N번째" → 호출되면 "차례가 되었습니다. 데스크로 와 주세요."
//  - 다녀간 적 있음(old) = 이름·오신 이유만 / 처음 옴(new) = 이름·생년월일·성별·휴대폰·주소·오신 이유 (종이 접수 카드 대체, 휴대폰 인증 없음)
//  - 서버 계약: services/receptionApi.js submitDeskReception · getReceptionTicket. 응답은 등록 여부와 무관하게 같은 모양 — 화면도 기존 등록 사실을 드러내지 않는다
//  - 대기표 ticket 만 sessionStorage(새로고침 대비, 오늘 KST 한정). 이름·번호·주소 등 입력값은 저장하지 않는다. 콘솔에 입력값 0
//  - 플래그 config/features.deskReception 단독(strict) OFF 면 "준비 중" — 서버도 같은 키로 feature-off(이중)
//  - 종전 웹 접수(clinicWalkin, 휴대폰 필수·앱 연결 안내) 화면은 이 흐름으로 대체됨. 만 14세 체크는 유지(10-03 대표)
//  - 10-04 정본(docs/정본_데스크접수_동의·방침_2026-10-04.md §1·§3-1·§7-3) 반영: 머리 「{치과명} 접수」+운영·위탁 줄, 꼬리 권리 행사 창구 줄,
//    "누가 적나요?" [본인이 적어요](self14 — 만 14세 체크) / [부모님이 대신 적어요](guardian — 보호자 이름·관계 부·모·후견인, 14세 체크 숨김).
//    처음 온 환자는 생년월일과 모드가 어긋나면 화면에서 먼저 안내(서버 guardian-required 와 같은 판정, 접수일 KST 만 나이).
//    보호자 판 정본·해시 미확정(isGuardianConsentReady=false)이면 보호자 선택 시 "준비 중 — 데스크에서 접수" 안내(서버 guardian-not-ready 와 같음)
//  - 의료법: 혜택·할인·무료 표현 없음
//  - 덴클 케어 1차 W4(2026-10-04): 접수 완료(대기) 화면에 앱 안내 한 줄 + [앱 받기](정본 K-1). 스토어 출시 전 숨김(careAppStoreLive 플래그 && 스토어 주소 —
//    config/careApp.js). 14세 미만 보호자 접수는 숨김, 새로고침으로 돌아온 대기 화면(작성 방식을 모름)도 숨김(보수적). 포스터에는 넣지 않는다.
//    파일럿 치과 한정(10-04 검증 4번): resolveClinicWalkin 응답 careAppPilot === true(서버 케어 게이트 판정 — 파일럿·위탁 확인·철회 등)일 때만.
//    fail-closed — 키 없음(구 서버)·조회 실패·false 는 숨김.
//  - 2026-10-05(대표 "권고대로"): ①앱 안내를 정본 v2.2 K-2(K-2a~j) 블록으로 교체 — 표시 조건은 위와 동일, 기기별 스토어 버튼(config/careApp.js)
//    ②덴클 케어 디자인 적용(IBM Plex Sans KR 400·600·700 자체 호스팅·배경 #F5F6F3·카드 #FFFFFF/#E3E6E1 r20·주색 #0F6655). 스타일만 — 문구·순서·동의·검증·동작 변경 없음
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SiteFooter from '../common/SiteFooter';
import { resolveClinicWalkin } from '../../services/clinicWalkinApi';
import { submitDeskReception, getReceptionTicket } from '../../services/receptionApi';
import { getFeatures, isDeskReceptionOn, isCareSearchLive } from '../../utils/features';
import { isCareStoreReady, walkinCareButtons, WALKIN_CARE_TITLE, WALKIN_CARE_INTRO, WALKIN_CARE_CHOICE, walkinCareHowto, walkinCareFeatures } from '../../config/careApp';
import { walkinHeaderLine, walkinFooterLine } from '../../utils/clinicWalkin';
import {
    RECEPTION_REASONS, RECEPTION_SEXES, RECEPTION_POLL_MS, RECEPTION_NAME_HINT, RECEPTION_ADDR_MAX, GUARDIAN_RELATIONS,
    receptionConsentFor, isGuardianConsentReady, isUnder14Kst, consentDetailText,
    isValidReceptionName, isValidReceptionTel, formatReceptionTelInput, formatBirthInput, isValidBirth, isValidAddr,
    receptionPatientError, RECEPTION_SERVER_NOT_READY, kstDayKey, fmtHM, CLINIC_CONSENT_REQUIRED_MSG,
} from '../../utils/deskReception';

// 모드-생년월일 불일치 안내(정본 §7-3 원문)
const MSG_SELF_UNDER14 = '생년월일을 보면 만 14세 미만입니다. 위에서 [부모님이 대신 적어요]를 눌러 부모님이 적어 주세요.';
const MSG_GUARDIAN_OVER14 = '생년월일을 보면 만 14세 이상입니다. 위에서 [본인이 적어요]를 눌러 주세요. 혼자 적기 어려우시면 데스크에 말씀해 주세요.';
// 보호자 판 미확정(서버 guardian-not-ready 와 같은 결과)
const MSG_GUARDIAN_NOT_READY = '만 14세 미만은 지금은 보호자와 함께 데스크에서 접수해 주세요.';

// 덴클 케어 글꼴 — 같은 도메인 자체 호스팅(public/fonts/ibm-plex-sans-kr, OFL 1.1). 외부 글꼴 서버 접속 금지(환자 IP가 제3자로 가지 않게).
// 이 화면에 들어올 때만 CSS 링크를 한 번 붙인다. 글자 묶음별 unicode-range 라 화면에 쓰인 글자 파일만 받는다. 실패해도 대체 글꼴로 표시
const CARE_FONT_LINK_ID = 'dencl-care-font-plex-kr';
const CARE_FONT_HREF = `${process.env.PUBLIC_URL || ''}/fonts/ibm-plex-sans-kr/ibm-plex-sans-kr.css`;
const CARE_FONT = "'IBM Plex Sans KR', 'Apple SD Gothic Neo', 'Malgun Gothic', 'Noto Sans KR', system-ui, sans-serif";
function useCareFont() {
    useEffect(() => {
        try {
            if (document.getElementById(CARE_FONT_LINK_ID)) return;
            const l = document.createElement('link');
            l.id = CARE_FONT_LINK_ID; l.rel = 'stylesheet'; l.href = CARE_FONT_HREF;
            l.onerror = () => console.error('[접수] 글꼴 불러오기 실패 — 대체 글꼴로 표시');
            document.head.appendChild(l);
        } catch (e) {
            console.error('[접수] 글꼴 링크 추가 실패 — 대체 글꼴로 표시:', e?.message || e);
        }
    }, []);
}

const ticketKey = (slug) => `deskTicket:${slug}`;
function loadTicket(slug) {
    try {
        const raw = sessionStorage.getItem(ticketKey(slug));
        if (!raw) return null;
        const t = JSON.parse(raw);
        if (t?.day === kstDayKey() && t?.ticket) return t;
        sessionStorage.removeItem(ticketKey(slug));
    } catch (e) {
        console.error('[접수] 대기표 복원 실패:', e?.message || e);
    }
    return null;
}
function saveTicket(slug, ticket) {
    try { sessionStorage.setItem(ticketKey(slug), JSON.stringify({ ticket, day: kstDayKey() })); }
    catch (e) { console.error('[접수] 대기표 저장 실패(새로고침 시 대기 화면이 사라질 수 있음):', e?.message || e); }
}
function clearTicket(slug) {
    try { sessionStorage.removeItem(ticketKey(slug)); }
    catch (e) { console.error('[접수] 대기표 정리 실패:', e?.message || e); }
}

// who: ''(미선택) | 'self14'(본인이 적어요) | 'guardian'(부모님이 대신 적어요) · gName·gRel = 보호자 이름·관계(guardian 만)
const EMPTY = { who: '', name: '', reason: '', birth: '', sex: '', tel: '', addr: '', gName: '', gRel: '', agree: false, age14: false };

function ClinicWalkin() {
    const { slug } = useParams();
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const [phase, setPhase] = useState('loading');   // loading | off | invalid | notready | start | old | new | wait
    const [clinicName, setClinicName] = useState('');
    const [loadMsg, setLoadMsg] = useState('');
    const [d, setD] = useState(EMPTY);
    const [showDetail, setShowDetail] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [ticket, setTicket] = useState(null);      // { ticket, order, called, calledAt, closed }
    const [myName, setMyName] = useState('');        // 호출 화면 표시용(메모리만)
    const [pollErr, setPollErr] = useState('');
    const pollErrRef = useRef('');
    const [careStoreReady, setCareStoreReady] = useState(false);   // W4 — 덴클 케어 출시 플래그 && 스토어 주소
    const [careSearchLive, setCareSearchLive] = useState(false);   // 법적보안 v2.8-5 — 「치과 찾기」 운영 플래그(기본·조회 실패 = 꺼짐 → QR 전용 문장)
    const [careAppPilot, setCareAppPilot] = useState(false);       // resolveClinicWalkin careAppPilot(불리언) — 이 치과가 덴클 케어 파일럿(게이트 통과)인가
    const [careEligible, setCareEligible] = useState(false);       // 이번 화면에서 본인(만 14세 이상)이 접수한 경우만 true(메모리만)
    useCareFont();

    useEffect(() => {
        let alive = true;
        (async () => {
            let flags = null;
            try { flags = await getFeatures(); }
            catch (e) { console.error('[접수] 플래그 조회 실패:', e?.code || e?.message || e); }
            if (!alive) return;
            if (!isDeskReceptionOn(flags)) { setPhase('off'); return; }
            setCareStoreReady(isCareStoreReady(flags));
            setCareSearchLive(isCareSearchLive(flags));
            if (!slug) { setPhase('invalid'); setLoadMsg('확인할 수 없는 QR입니다. 데스크에 말씀해 주세요.'); return; }
            // 치과명 = 서버 조회 결과만(URL 에서 꺼내 보여주지 않음). resolveClinicWalkin 은 종전 clinicWalkin 키를 본다 —
            // 그 키가 꺼져 feature-off 면 치과명 없이 진행(접수 서버가 slug 를 최종 판정). dev-server 에 deskReception 수용 요청함
            // deskReady(10-04 서버 계약): false = 치과 처리위탁 확인 전 → 입력 화면 대신 안내. 응답에 키가 없으면(구 서버) 기존 동작
            let deskNotReady = false;
            try {
                const r = await resolveClinicWalkin(slug);
                if (!alive) return;
                const nm = String(r?.clinicName || '').trim();
                if (r?.ok === true && nm) setClinicName(nm);
                setCareAppPilot(r?.ok === true && r?.careAppPilot === true);
                if (r?.ok === true && r?.deskReady === false) deskNotReady = true;
                else if (r?.ok === false) { setPhase('invalid'); setLoadMsg('확인할 수 없는 QR입니다. 데스크에 말씀해 주세요.'); return; }
            } catch (e) {
                if (!alive) return;
                console.error('[접수] 치과 조회 실패:', e?.code || e?.message || e, e?.details?.code || '');   // slug·입력값 기록 금지
                const code = String(e?.code || '').replace(/^functions\//, '');
                if (code === 'not-found' && e?.details?.code) { setPhase('invalid'); setLoadMsg('확인할 수 없는 QR입니다. 데스크에 말씀해 주세요.'); return; }
                // feature-off·미배포 등은 치과명 없이 진행
            }
            const saved = loadTicket(slug);
            // 이미 받은 대기표가 있으면 대기 화면 유지(대기표 판정은 서버) — 새 접수만 막는다
            if (saved) { setTicket({ ticket: saved.ticket }); setPhase('wait'); }
            else if (deskNotReady) setPhase('notready');
            else setPhase('start');
        })();
        return () => { alive = false; };
    }, [slug]);

    // 대기표 폴링 — 호출됨·마감이면 멈춘다
    const ticketId = ticket?.ticket || '';
    const stopped = !!(ticket?.called || ticket?.closed);
    useEffect(() => {
        if (phase !== 'wait' || !ticketId || stopped) return undefined;
        let alive = true;
        const poll = async () => {
            if (document.hidden) return;
            try {
                const r = await getReceptionTicket(slug, ticketId);
                if (!alive) return;
                setTicket(prev => ({ ...(prev || {}), ticket: ticketId, order: r?.order, called: r?.called === true, calledAt: r?.calledAt, closed: r?.closed === true }));
                if (r?.closed === true) clearTicket(slug);
                if (pollErrRef.current) { pollErrRef.current = ''; setPollErr(''); }
            } catch (e) {
                if (!alive) return;
                const msg = receptionPatientError(e, '대기 순서를 확인하지 못했습니다. 잠시 후 다시 확인합니다.');
                if (msg !== pollErrRef.current) console.error('[접수] 대기표 조회 실패:', e?.code || e?.message || e, e?.details?.code || '');
                pollErrRef.current = msg; setPollErr(msg);
                if (e?.details?.code === 'ticket-not-found') { clearTicket(slug); setTicket(prev => ({ ...(prev || {}), closed: true })); }
            }
        };
        poll();
        const t = setInterval(poll, RECEPTION_POLL_MS);
        return () => { alive = false; clearInterval(t); };
    }, [phase, ticketId, stopped, slug]);

    const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));
    const go = (p) => { setPhase(p); setErr(''); setShowDetail(false); };

    // 처음 온 환자 — 생년월일과 모드가 어긋나는지(화면 판정, 서버와 같은 KST 만 나이). 다녀간 환자는 생년월일이 없어 판정하지 않는다
    const modeMismatch = (kind) => {
        if (kind !== 'new' || !d.who || !isValidBirth(d.birth)) return '';
        const under = isUnder14Kst(d.birth);
        if (under === true && d.who === 'self14') return MSG_SELF_UNDER14;
        if (under === false && d.who === 'guardian') return MSG_GUARDIAN_OVER14;
        return '';
    };

    const submit = async (kind) => {
        if (busy) return;
        const guardianMode = d.who === 'guardian';
        if (!d.who) { setErr('누가 적는지 먼저 골라 주세요.'); return; }
        if (guardianMode && !isGuardianConsentReady(kind)) { setErr(MSG_GUARDIAN_NOT_READY); return; }
        const miss = [];
        if (!d.name.trim()) miss.push(guardianMode ? '아이 이름' : '이름');
        if (!d.reason) miss.push('오신 이유');
        if (kind === 'new') {
            if (!d.birth) miss.push(guardianMode ? '아이 생년월일' : '생년월일');
            if (!d.sex) miss.push(guardianMode ? '아이 성별' : '성별');
            if (!d.tel) miss.push(guardianMode ? '연락받을 휴대폰 번호' : '휴대폰 번호');
            if (!d.addr.trim()) miss.push(guardianMode ? '아이 주소' : '주소');
        }
        if (guardianMode) {
            if (!d.gName.trim()) miss.push('보호자 이름');
            if (!d.gRel) miss.push('아이와의 관계');
        } else if (!d.age14) miss.push('만 14세 이상 확인');
        if (!d.agree) miss.push('동의 체크');
        if (miss.length) { setErr(`${miss.join(', ')}을(를) 채워 주세요.`); return; }
        if (!isValidReceptionName(d.name)) { setErr(`이름을 확인해 주세요 (${RECEPTION_NAME_HINT}).`); return; }
        if (kind === 'new') {
            if (!isValidBirth(d.birth)) { setErr('생년월일을 1970-01-01 형식으로 적어 주세요.'); return; }
            const mm = modeMismatch(kind);
            if (mm) { setErr(mm); return; }
            if (!isValidReceptionTel(d.tel)) { setErr('휴대폰 번호를 010-0000-0000 형식으로 적어 주세요.'); return; }
            if (!isValidAddr(d.addr)) { setErr(`주소를 ${RECEPTION_ADDR_MAX}자 이내로 적어 주세요.`); return; }
        }
        if (guardianMode && (!isValidReceptionName(d.gName) || d.gName.replace(/\s+/g, '').length < 2)) {
            setErr(`보호자 이름을 확인해 주세요 (${RECEPTION_NAME_HINT}).`); return;
        }
        setBusy(true); setErr('');
        try {
            const c = receptionConsentFor(kind, d.who);
            const r = await submitDeskReception({
                slug, kind, name: d.name, reason: d.reason,
                ...(kind === 'new' ? { birth: d.birth, sex: d.sex, tel: d.tel, addr: d.addr } : {}),
                consent: guardianMode
                    ? { version: c.version, textHash: c.textHash, ageMode: 'guardian', guardian: { name: d.gName, relation: d.gRel } }
                    : { version: c.version, textHash: c.textHash, ageMode: 'self14', age14Checked: d.age14 === true },
            });
            const tk = String(r?.ticket || '');
            if (!tk) {
                console.error('[접수] 응답에 대기표 없음');
                setErr('접수하지 못했습니다. 데스크에 말씀해 주세요.');
                return;
            }
            saveTicket(slug, tk);
            setMyName(d.name.normalize('NFC').trim());
            setCareEligible(!guardianMode);   // 보호자(14세 미만) 접수는 앱 안내 숨김(정본 K-1)
            setD(EMPTY);   // 입력값은 메모리에서 비운다(뒤로 가기 시 재노출 방지)
            setTicket({ ticket: tk, order: r?.order, called: false, closed: false });
            setPhase('wait');
        } catch (e) {
            console.error('[접수] 접수 실패:', e?.code || e?.message || e, e?.details?.code || '');   // 입력값 기록 금지
            setErr(receptionPatientError(e));
        } finally {
            setBusy(false);
        }
    };

    const restart = () => { clearTicket(slug); setTicket(null); setMyName(''); setCareEligible(false); setPollErr(''); pollErrRef.current = ''; go('start'); };
    const title = clinicName ? `${clinicName} 접수` : '접수';

    const field = (k, label, ph, opts = {}) => (
        <div style={W.f}>
            <label style={W.label} htmlFor={`rc-${k}`}>{label}</label>
            <input id={`rc-${k}`} type={opts.type || 'text'} inputMode={opts.inputMode} value={d[k]} maxLength={opts.max || 40}
                autoComplete="off" placeholder={ph}
                onChange={e => set(k, opts.format ? opts.format(e.target.value) : e.target.value)} style={W.input} />
            {opts.help && <div style={W.hint}>{opts.help}</div>}
        </div>
    );
    const seg = (k, options) => (
        <div style={W.seg}>
            {options.map(o => {
                const on = d[k] === o.key;
                return <button key={o.key} type="button" aria-pressed={on} onClick={() => set(k, o.key)} style={{ ...W.segBtn, ...(on ? W.segOn : null) }}>{o.label}</button>;
            })}
        </div>
    );
    // "누가 적나요?" (정본 §7-3 원문) — 입력 칸보다 먼저. 고르면 칸 라벨이 바뀐다
    const whoBox = () => {
        const opt = (key, big, small) => {
            const on = d.who === key;
            return (
                <button type="button" aria-pressed={on} onClick={() => { setD(prev => ({ ...prev, who: key, agree: false, age14: false })); setErr(''); setShowDetail(false); }}
                    style={{ ...W.whoBtn, ...(on ? W.whoOn : null) }}>
                    <span style={{ display: 'block', fontSize: 17, fontWeight: 700 }}>{big}</span>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 400, marginTop: 2, color: on ? C.pri : C.sub }}>{small}</span>
                </button>
            );
        };
        return (
            <div style={W.f}>
                <div style={W.label}>누가 적나요?</div>
                {opt('self14', '본인이 적어요', '환자 본인 · 만 14세 이상')}
                {opt('guardian', '부모님이 대신 적어요', '환자가 만 14세 미만일 때 · 부모 또는 후견인')}
                <div style={W.hint}>할머니·할아버지 등 부모나 후견인이 아닌 분이 아이와 오셨다면 데스크에서 접수해 주세요.</div>
            </div>
        );
    };
    // 동의 영역 — 문안은 정본(utils/deskReception.js). label 은 치환 없이, [내용 보기] 상세만 {clinicName} 치환
    const agreeBox = (kind) => {
        const c = receptionConsentFor(kind, d.who);
        return (
            <div style={{ display: 'grid', gap: 10 }}>
                <label style={W.agree}>
                    <input type="checkbox" checked={d.agree} onChange={e => set('agree', e.target.checked)} style={W.checkbox} />
                    <span>{c.label}{' '}
                        <button type="button" onClick={(e) => { e.preventDefault(); setShowDetail(v => !v); }} style={W.linkBtn}>{showDetail ? '내용 닫기' : '내용 보기'}</button>
                    </span>
                </label>
                {showDetail && <div style={W.detail}>{consentDetailText(c, clinicName)}</div>}
                {/* 본인 모드만 만 14세 체크(동의 체크 아래, 정본 §7-3). 보호자 모드는 동의 label 의 진술이 대신한다 */}
                {d.who === 'self14' && (
                    <label style={W.agree}>
                        <input type="checkbox" checked={d.age14} onChange={e => set('age14', e.target.checked)} style={W.checkbox} />
                        <span>[필수] 만 14세 이상입니다. <span style={{ color: C.sub }}>만 14세 미만이면 위에서 [부모님이 대신 적어요]를 눌러 주세요.</span></span>
                    </label>
                )}
            </div>
        );
    };
    // 모드 선택 후 입력 영역 — 보호자 판 미확정이면 안내만
    const formBody = (kind) => {
        if (!d.who) return null;
        const g = d.who === 'guardian';
        if (g && !isGuardianConsentReady(kind)) {
            return (
                <div style={W.notice}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>준비 중입니다</div>
                    {MSG_GUARDIAN_NOT_READY}
                </div>
            );
        }
        const mm = modeMismatch(kind);
        return (
            <>
                {field('name', g ? '아이 이름' : '이름', '홍길동', { max: 20 })}
                {kind === 'new' && (
                    <>
                        {field('birth', g ? '아이 생년월일' : '생년월일', '1970-01-01', { inputMode: 'numeric', format: formatBirthInput, max: 10 })}
                        {mm && <div style={W.warn}>{mm}</div>}
                        <div style={W.f}><div style={W.label}>{g ? '아이 성별' : '성별'}</div>{seg('sex', RECEPTION_SEXES)}</div>
                        {field('tel', g ? '연락받을 휴대폰 번호' : '휴대폰 번호', '010-0000-0000', { type: 'tel', inputMode: 'numeric', format: formatReceptionTelInput, max: 13, help: g ? '아이 번호나 보호자 번호를 적어 주세요.' : '' })}
                        {field('addr', g ? '아이 주소' : '주소', '시·구·동까지', { max: RECEPTION_ADDR_MAX })}
                    </>
                )}
                <div style={W.f}><div style={W.label}>오늘 오신 이유</div>{seg('reason', RECEPTION_REASONS)}</div>
                {g && (
                    <>
                        {field('gName', '보호자 이름', '', { max: 20, help: '동의하시는 분의 이름' })}
                        <div style={W.f}><div style={W.label}>아이와의 관계</div>{seg('gRel', GUARDIAN_RELATIONS)}<div style={W.hint}>후견인: 법원이 정한 후견인</div></div>
                    </>
                )}
                {agreeBox(kind)}
                {err && <div style={W.err}>{err}</div>}
                <button type="button" disabled={busy} onClick={() => submit(kind)} style={{ ...W.bigPri, opacity: busy ? 0.5 : 1 }}>{busy ? '접수 중...' : (kind === 'new' ? '접수하기' : '대기 순서 받기')}</button>
            </>
        );
    };

    const serverNotReady = pollErr.startsWith(RECEPTION_SERVER_NOT_READY);
    const careBtns = walkinCareButtons();   // 기기 판별은 브라우저 안에서만 — 서버 전송·저장 없음

    return (
        <div style={W.page}>
            <div style={W.header}>
                <span style={W.brand}>{title}</span>
                <div style={W.headerLine}>{walkinHeaderLine(clinicName)}</div>
            </div>
            <div style={W.body}>
                {phase === 'loading' && <div style={W.center}>확인 중...</div>}

                {phase === 'off' && (
                    <div style={W.card}>
                        <h2 style={W.h2}>준비 중입니다</h2>
                        <p style={W.text}>지금은 QR 접수를 받지 않습니다. 데스크에 말씀해 주세요.</p>
                    </div>
                )}

                {phase === 'invalid' && (
                    <div style={W.card}>
                        <h2 style={W.h2}>확인할 수 없는 QR입니다</h2>
                        <p style={W.text}>{loadMsg}</p>
                    </div>
                )}

                {phase === 'notready' && (
                    <div style={W.card}>
                        <h2 style={W.h2}>QR 접수 준비 중입니다</h2>
                        <p style={W.text}>{CLINIC_CONSENT_REQUIRED_MSG}</p>
                    </div>
                )}

                {phase === 'start' && (
                    <div style={{ ...W.card, display: 'grid', gap: 12 }}>
                        <h2 style={{ ...W.h2, margin: 0 }}>{title}</h2>
                        <div style={W.lead}>어느 쪽인가요?</div>
                        <button type="button" onClick={() => go('new')} style={{ ...W.bigPri, ...W.choice }}>처음 왔어요</button>
                        <button type="button" onClick={() => go('old')} style={{ ...W.bigGhost, ...W.choice }}>다녀간 적 있어요</button>
                        <div style={W.hint}>다녀간 적 있으면 이름과 오신 이유만 적으면 대기 순서가 잡힙니다.</div>
                    </div>
                )}

                {phase === 'old' && (
                    <div style={{ ...W.card, display: 'grid', gap: 14 }}>
                        <button type="button" onClick={() => go('start')} style={W.back}>{'‹ 처음으로'}</button>
                        <h2 style={{ ...W.h2, margin: 0 }}>대기 순서 받기</h2>
                        {whoBox()}
                        {formBody('old')}
                        {!d.who && err && <div style={W.err}>{err}</div>}
                    </div>
                )}

                {phase === 'new' && (
                    <div style={{ ...W.card, display: 'grid', gap: 14 }}>
                        <button type="button" onClick={() => go('start')} style={W.back}>{'‹ 처음으로'}</button>
                        <h2 style={{ ...W.h2, margin: 0 }}>처음 오셨군요</h2>
                        <div style={W.lead}>종이 접수 카드 대신 여기에 적어 주세요.</div>
                        {whoBox()}
                        {formBody('new')}
                        {!d.who && err && <div style={W.err}>{err}</div>}
                    </div>
                )}

                {phase === 'wait' && (
                    <div style={{ ...W.card, display: 'grid', gap: 12 }}>
                        {ticket?.closed ? (
                            <>
                                <h2 style={{ ...W.h2, margin: 0 }}>오늘 접수가 마감되었습니다</h2>
                                <div style={W.lead}>다시 오시면 QR을 새로 찍어 주세요.</div>
                                <button type="button" onClick={restart} style={W.bigGhost}>처음으로</button>
                            </>
                        ) : ticket?.called ? (
                            <>
                                <h2 style={{ ...W.h2, margin: 0 }}>{myName ? `${myName} 님` : title}</h2>
                                <div style={W.callbox}>차례가 되었습니다.<br />데스크로 와 주세요.</div>
                                {fmtHM(ticket?.calledAt) && <div style={{ ...W.hint, textAlign: 'center' }}>{`${fmtHM(ticket.calledAt)} 호출`}</div>}
                            </>
                        ) : (
                            <>
                                <h2 style={{ ...W.h2, margin: 0, textAlign: 'center' }}>접수되었습니다</h2>
                                <div style={{ ...W.hint, textAlign: 'center' }}>지금 대기</div>
                                <div style={W.waitnum}>{Number.isFinite(Number(ticket?.order)) && ticket?.order != null ? Number(ticket.order) : '-'}</div>
                                <div style={{ ...W.hint, textAlign: 'center' }}>번째입니다</div>
                                <div style={{ ...W.lead, textAlign: 'center' }}>자리에 앉아 기다리시면 이름을 불러 드립니다.<br />이 화면은 차례가 오면 바뀝니다.</div>
                                {pollErr && <div style={serverNotReady ? W.err : { ...W.hint, color: '#9a5b00', textAlign: 'center' }}>{pollErr}</div>}
                            </>
                        )}
                    </div>
                )}

                {/* 덴클 케어 앱 안내(정본 v2.2 K-2a~j, 종전 K-1 대체) — 대기·호출 화면에서만, 출시 전·파일럿 밖 치과·보호자 접수·새로고침 복귀 시 숨김.
                    기기에서 쓸 스토어 버튼이 없으면 블록 전체 숨김. 링크 = 스토어 주소 그대로(식별값 없음)·새 탭·noreferrer. 누름 기록 없음 */}
                {phase === 'wait' && !ticket?.closed && careStoreReady && careAppPilot && careEligible && careBtns.buttons.length > 0 && (
                    <div style={{ ...W.card, display: 'grid', gap: 12 }}>
                        <div style={W.careTitle}>{WALKIN_CARE_TITLE}</div>
                        <div style={W.careSub}>{WALKIN_CARE_INTRO}</div>
                        <div style={{ display: 'grid', gap: 10 }}>
                            {walkinCareFeatures(clinicName).map(f => (
                                <div key={f.label} style={W.careRow}>
                                    <span aria-hidden="true" style={W.careTick}>{'✓'}</span>
                                    <div style={W.careText}><b>{f.label}</b>{' '}{f.text}</div>
                                </div>
                            ))}
                        </div>
                        <div style={W.careSub}>{WALKIN_CARE_CHOICE}</div>
                        <div style={W.careSub}>{walkinCareHowto(clinicName, careSearchLive)}</div>
                        <div style={{ display: 'grid', gap: 8 }}>
                            {careBtns.buttons.map(b => (
                                <a key={b.href} href={b.href} target="_blank" rel="noreferrer" style={W.storeBtn}>{b.text}</a>
                            ))}
                        </div>
                        <div style={{ ...W.careSub, textAlign: 'center', marginTop: -4 }}>{careBtns.note}</div>
                    </div>
                )}

            </div>
            <div style={W.footLine}>{walkinFooterLine(clinicName)}</div>
            <SiteFooter />
        </div>
    );
}

// 폰 화면 우선 — 50~60대 기준: 본문 15px·보조 14px 이상, 큰 버튼 52px·선택 버튼 64px.
// 덴클 케어 디자인(2026-10-05, 참고 dencl_care_design_match.html #flow 1~4): 배경 #F5F6F3, 카드 #FFFFFF/#E3E6E1 r20, 본문 #17201C, 보조 #56605B, 주색 #0F6655(연함 #E3F0EC)
const C = { bg: '#F5F6F3', card: '#FFFFFF', line: '#E3E6E1', ink: '#17201C', sub: '#56605B', pri: '#0F6655', priSoft: '#E3F0EC' };
const W = {
    page: { minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column', fontFamily: CARE_FONT, color: C.ink },
    header: { background: C.card, borderBottom: `1px solid ${C.line}`, padding: '14px 16px' },
    brand: { fontSize: 17, fontWeight: 700, color: C.ink },
    headerLine: { fontSize: 14, color: C.sub, lineHeight: 1.55, marginTop: 4 },
    footLine: { maxWidth: 520, width: '100%', margin: '0 auto', padding: '0 16px 12px', boxSizing: 'border-box', fontSize: 14, color: C.sub, lineHeight: 1.6 },
    whoBtn: { width: '100%', minHeight: 64, padding: '12px 16px', borderRadius: 14, border: `1px solid ${C.line}`, background: C.card, color: C.ink, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' },
    whoOn: { background: C.priSoft, border: `2px solid ${C.pri}`, color: C.pri },
    warn: { fontSize: 15, color: '#8A3D10', background: '#fbeee4', borderRadius: 12, padding: '12px 14px', lineHeight: 1.55 },
    notice: { fontSize: 15, color: C.sub, background: C.bg, border: `1px solid ${C.line}`, borderRadius: 14, padding: '14px 14px', lineHeight: 1.6 },
    body: { flex: 1, maxWidth: 520, width: '100%', margin: '0 auto', padding: '20px 16px', boxSizing: 'border-box' },
    card: { backgroundColor: C.card, borderRadius: 20, border: `1px solid ${C.line}`, padding: 20, marginBottom: 14 },
    h2: { fontSize: 22, fontWeight: 700, color: C.ink, margin: '0 0 8px 0', lineHeight: 1.35 },
    text: { fontSize: 16, color: C.sub, lineHeight: 1.6, margin: 0 },
    lead: { fontSize: 15, color: C.sub, lineHeight: 1.6 },
    hint: { fontSize: 14, color: C.sub, lineHeight: 1.6 },
    f: { display: 'grid', gap: 6 },
    label: { fontSize: 15, fontWeight: 600, color: C.sub },
    input: { width: '100%', minHeight: 52, padding: '12px 14px', fontSize: 18, border: `1px solid ${C.line}`, borderRadius: 12, boxSizing: 'border-box', outline: 'none', background: C.card, color: C.ink, fontFamily: 'inherit' },
    seg: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    segBtn: { minHeight: 48, padding: '10px 16px', fontSize: 17, borderRadius: 12, border: `1px solid ${C.line}`, background: C.card, color: C.ink, cursor: 'pointer', minWidth: 64, fontFamily: 'inherit' },
    segOn: { background: C.priSoft, border: `2px solid ${C.pri}`, color: C.pri, fontWeight: 700 },
    agree: { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15, color: C.ink, lineHeight: 1.55, cursor: 'pointer' },
    checkbox: { width: 22, height: 22, marginTop: 1, flexShrink: 0, accentColor: C.pri },
    linkBtn: { background: 'none', border: 'none', padding: 0, color: C.pri, textDecoration: 'underline', fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' },
    detail: { marginTop: 8, fontSize: 14, color: C.sub, lineHeight: 1.65, whiteSpace: 'pre-line', wordBreak: 'keep-all', background: C.bg, border: `1px solid ${C.line}`, borderRadius: 12, padding: '12px 14px' },
    err: { fontSize: 15, color: '#b3261e', background: '#fde8e6', borderRadius: 12, padding: '12px 14px', lineHeight: 1.5 },
    bigPri: { width: '100%', minHeight: 52, padding: '12px 16px', borderRadius: 14, border: 'none', background: C.pri, color: '#FFFFFF', fontSize: 17, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
    bigGhost: { width: '100%', minHeight: 52, padding: '12px 16px', borderRadius: 14, border: `1px solid ${C.line}`, background: C.card, color: C.ink, fontSize: 17, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
    choice: { minHeight: 64, fontSize: 18 },   // 처음 왔어요 / 다녀간 적 있어요
    back: { background: 'none', border: 'none', color: C.sub, fontSize: 15, cursor: 'pointer', padding: 0, textAlign: 'left', fontFamily: 'inherit' },
    waitnum: { fontFamily: 'inherit', fontSize: 72, fontWeight: 700, textAlign: 'center', lineHeight: 1, color: C.pri },
    callbox: { background: '#fde8e6', color: '#b3261e', borderRadius: 14, padding: 22, textAlign: 'center', fontSize: 21, fontWeight: 700, lineHeight: 1.5 },
    careTitle: { fontSize: 17, fontWeight: 700, color: C.ink, lineHeight: 1.45 },
    careSub: { fontSize: 14, color: C.sub, lineHeight: 1.6 },
    careRow: { display: 'flex', gap: 10, alignItems: 'flex-start' },
    careTick: { width: 22, height: 22, borderRadius: '50%', background: C.priSoft, color: C.pri, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', fontWeight: 700, fontSize: 13, marginTop: 1 },
    careText: { fontSize: 15, color: C.ink, lineHeight: 1.6 },
    storeBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 52, padding: '12px 16px', borderRadius: 14, background: C.pri, color: '#FFFFFF', fontSize: 17, fontWeight: 600, textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' },
    center: { minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.sub, fontSize: 16 },
};

export default ClinicWalkin;
